/* eslint-disable no-fallthrough */
import { compose, createStore, combineReducers, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import persistState from 'redux-localstorage';

import { slicer } from 'state/util';
import { authReducer } from 'state/auth/reducer';
import { userReducer } from 'state/user/reducer';
import { registrationReducer } from 'state/registration/reducer';
import { resetPasswordReducer } from 'state/resetPassword/reducer';
import { subscriberReducer } from 'state/subscriber/reducer';
import { donationReducer } from 'state/donation/reducer';
import { locationReducer } from 'state/location/reducer';
import { votePrefsReducer } from 'state/votePrefs/reducer';
import { ballotsReducer } from 'state/ballots/reducer';
import { commentsReducer } from 'state/comments/reducer';
import { commentVisibilityReducer } from 'state/commentVisibility/reducer';
import { votePrefsSlicer } from 'state/votePrefs/slicer';
import { searchReducer } from 'state/search/reducer';
import { requestStatusReducer } from 'state/requestStatus/reducer';
import { flagReducer } from 'state/flag/reducer';
import { promptReducer } from 'state/prompt/reducer';

/**
 * Top level state and action reducer for our API related
 * actions.
 *
 * Global State Layout
 * {
 *   # null when not logged in
 *   user: {
 *     data: {
 *       username: ...
 *       ... user properties ...
 *     },
 *     message: null|"update status"
 *   },
 *   auth: {
 *     loggedIn: true|false
 *     token: "authToken"|null
 *     message: "a status message"|null
 *   },
 *   registration: {
 *     # status describes state of registration:
 *     status: NONE|AWAITING_VERIFICATION|COMPLETE,
 *     message: ...
 *   },
 *   resetPassword: {
 *     status: NONE|AWAITING_VERIFICATION|COMPLETE,
 *     message: ...,
 *   },
 *   //  just used to relay messages on subscriber creation
 *   subscriber: {
 *     status: NONE|SUCCESS|FAILURE
 *     message: ...,
 *   },
 *   donation: {
 *     status: NONE|SUCCESS|FAILURE
 *     message: ...,
 *   },
 *   location: {
        status: NONE|PENDING|SET,
        location: SF|SJ|SJCounty|CA|null
     }
 *   }
 *   votePrefs: {
 *     "{pageId}#{userId}": {
 *       data: {
 *         vote: "Y",
 *         shared: true,
 *         reason: "Some markdown..."
 *       },
 *       message: "Save succesful."
 *     },
 *     ... other pages of our own ...
 *   },
 *   # NOTE: this cannot be persisted to the state tree due
 *   # to the maximum size constraint on local storage and the
 *   # fact that this could easily explode out over the 5-10m limit
 *   comments: {
 *     "{pageId}#{userId}": {
 *       data: [{
 *         text: "comment text ...",
 *         sectionId: "010",
 *       },{
 *       ... more comments ...
 *       }],
 *       message: "Some status message for getting/updating/etc"
 *     }
 *   }
 * }
 *
 */

// top-level reducer. passes off to sub reducers which are
// specific to the domain (user, register, auth, etc)
const reducer = combineReducers({
  user: userReducer,
  registration: registrationReducer,
  auth: authReducer,
  resetPassword: resetPasswordReducer,
  subscriber: subscriberReducer,
  donation: donationReducer,
  locale: locationReducer,
  votePrefs: votePrefsReducer,
  ballots: ballotsReducer,
  comments: commentsReducer,
  commentVisibility: commentVisibilityReducer,
  search: searchReducer,
  requestStatus: requestStatusReducer,
  flag: flagReducer,
  prompt: promptReducer,
});

// state key paths to exclude from persisting to localStore
const slicers = {
  auth: slicer(['message']),
  locale: slicer(['payload']),
  user: slicer(['message']),
  registration: slicer(['message']),
  // TODO: remove messages from nested pageId objects
  votePrefs: votePrefsSlicer,
};

// configuration for our localStorage persistence
const persistConfig = {
  // this sets our state under the localStore bythebay key
  key: 'bythebay',
  // controls which parts of our state to persist, we
  // want to exclude status messages
  slicer: (paths) => {
    return (state) => {
      let newState = {};
      paths.forEach((key) => {
        newState[key] = slicers[key](state[key]);
      });
      return newState;
    };
  },
};

const sagaMiddleware = createSagaMiddleware();

const middlewares = [
  applyMiddleware(sagaMiddleware),
];

// don't add the persistence layer if we're not running in a browser
if (window.localStorage !== undefined)
  middlewares.push(persistState(Object.keys(slicers), persistConfig));

// set up our reducers, store and middleware
export const store = createStore(reducer, compose(...middlewares));

// easier way to begin a saga so we won't have to import
// react saga outside of this module
store.runSaga = sagaMiddleware.run;
export default store;




// WEBPACK FOOTER //
// ./src/state/store.js